import { useId, useState } from 'react';
import type { FormEvent } from 'react';
import type { OrganizationLicenceView } from '../../api';
import { useResource } from '../../hooks';
import { dataEntityLabel, licenceReasonText } from '../../labels';
import { Dialog, Input, Radio, Select, Textarea } from '../../components/primitives';
import { ConfirmDialog, ErrorNotice, Field, Skeleton, StatusBadge } from '../../components/ui';
import { staffApi } from '../../staffApi';
import { formatDate } from './StaffOrganizationsPage';

/**
 * The organization's store licence in the staff console (CO-143): staff
 * assign the plan, how it is paid, its grace period and domain limit, and
 * revoke it. Every change needs a note, recorded in staff activity.
 */
export function StaffLicencePanel({ organizationId, onChanged }: { organizationId: string; onChanged: (message: string) => void }) {
  const loaded = useResource(() => staffApi.licence(organizationId), [organizationId], organizationId);
  const plans = useResource(() => staffApi.licencePlans(), []);
  const [view, setView] = useState<OrganizationLicenceView | null>(null);
  const [editing, setEditing] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const data = view ?? loaded.data;
  const id = useId();

  return (
    <section className="panel" aria-labelledby={`${id}-title`} data-testid="staff-licence">
      <div className="staff-panel__head">
        <h2 id={`${id}-title`} className="staff-panel__title">
          Store licence
        </h2>
        {data ? (
          <div className="staff-panel__actions">
            {data.licence && !data.licence.revoked_at ? (
              <button className="button button--danger-quiet button--small" type="button" onClick={() => setRevoking(true)}>
                Revoke
              </button>
            ) : null}
            <button className="button button--small" type="button" onClick={() => setEditing(true)}>
              {data.licence ? 'Change licence' : 'Assign a plan'}
            </button>
          </div>
        ) : null}
      </div>
      {loaded.loading && !data ? (
        <div className="staff-licence__skeleton" aria-hidden="true">
          <Skeleton width="70%" />
          <Skeleton width="55%" />
          <Skeleton width="62%" />
        </div>
      ) : null}
      <ErrorNotice error={loaded.error} />
      {data ? <LicenceFacts view={data} /> : null}
      {editing && data ? (
        <LicenceDialog
          organizationId={organizationId}
          view={data}
          plans={plans.data?.plans ?? []}
          onClose={() => setEditing(false)}
          onSaved={(next) => {
            setView(next);
            setEditing(false);
            onChanged('The store licence was saved. Stores get it at their next check, within 15 minutes.');
          }}
        />
      ) : null}
      <RevokeDialog
        open={revoking}
        organizationId={organizationId}
        onClose={() => setRevoking(false)}
        onRevoked={(next) => {
          setView(next);
          setRevoking(false);
          onChanged('The store licence was revoked.');
        }}
      />
    </section>
  );
}

function LicenceFacts({ view }: { view: OrganizationLicenceView }) {
  const l = view.licence;
  const active = view.domains.filter((d) => d.status === 'active').length;
  return (
    <>
      <dl className="staff-facts">
        <div>
          <dt>Status</dt>
          <dd>
            <StatusBadge kind="licence" value={view.status} />
            {view.reason ? <div className="staff-table__sub">{licenceReasonText[view.reason] ?? view.reason}</div> : null}
            {view.grace_until ? <div className="staff-table__sub">Grace period until {formatDate(view.grace_until)}</div> : null}
          </dd>
        </div>
        <div>
          <dt>Plan</dt>
          <dd>{view.plan ? view.plan.name : <span className="muted">None</span>}</dd>
        </div>
        {l ? (
          <>
            <div>
              <dt>Paid by</dt>
              <dd>
                {l.source === 'stripe'
                  ? 'Stripe subscription'
                  : `Agreement${l.manual_valid_until ? `, until ${formatDate(l.manual_valid_until)}` : ', no end date'}`}
              </dd>
            </div>
            <div>
              <dt>Grace period</dt>
              <dd>{l.grace_days === 1 ? '1 day' : `${l.grace_days} days`}</dd>
            </div>
            <div>
              <dt>Stores send</dt>
              <dd>{view.entities.length ? view.entities.map((e) => dataEntityLabel[e] ?? e).join(', ') : 'Nothing'}</dd>
            </div>
            <div>
              <dt>Production domains</dt>
              <dd>
                {active} of {view.domain_limit}
                {l.overrides.production_domains !== undefined ? <span className="muted"> (set for this organization)</span> : null}
              </dd>
            </div>
          </>
        ) : null}
      </dl>
      {view.domains.length > 0 ? (
        <ul className="staff-licence__domains">
          {view.domains.map((d) => (
            <li key={d.id}>
              <span translate="no">{d.host || 'No public address'}</span> <StatusBadge kind="licensedDomain" value={d.status} />
              <span className="staff-table__sub">
                {d.project_name}
                {d.website_code ? `, website ${d.website_code}` : ''}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

function LicenceDialog({
  organizationId,
  view,
  plans,
  onClose,
  onSaved,
}: {
  organizationId: string;
  view: OrganizationLicenceView;
  plans: { code: string; name: string; production_domains: number; active: boolean }[];
  onClose: () => void;
  onSaved: (view: OrganizationLicenceView) => void;
}) {
  const l = view.licence;
  const [plan, setPlan] = useState(l?.plan_code ?? plans.find((p) => p.active && p.code !== 'legacy')?.code ?? plans[0]?.code ?? '');
  const [source, setSource] = useState<'manual' | 'stripe'>(l?.source ?? 'manual');
  const [validUntil, setValidUntil] = useState(l?.manual_valid_until ? l.manual_valid_until.slice(0, 10) : '');
  const [graceDays, setGraceDays] = useState(String(l?.grace_days ?? 7));
  const [domains, setDomains] = useState(l?.overrides.production_domains !== undefined ? String(l.overrides.production_domains) : '');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const chosen = plans.find((p) => p.code === plan);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onSaved(
        await staffApi.setLicence(organizationId, {
          plan_code: plan,
          source,
          manual_valid_until: source === 'manual' && validUntil ? new Date(`${validUntil}T23:59:59Z`).toISOString() : null,
          grace_days: Number(graceDays),
          overrides: { ...(l?.overrides ?? {}), production_domains: domains.trim() === '' ? undefined : Number(domains) },
          note,
        }),
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      size="md"
      title={l ? 'Change the store licence' : 'Assign a plan'}
      description="Stores get the change at their next check, within 15 minutes. Saving lifts a revocation."
      onClose={onClose}
      busy={busy}
      onSubmit={submit}
      testId="staff-licence-dialog"
      footer={
        <>
          <button type="button" className="button button--secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="button button--primary" disabled={busy || plan === '' || note.trim() === ''}>
            {busy ? 'Saving licence' : 'Save licence'}
          </button>
        </>
      }
    >
      <ErrorNotice error={error} />
      <Field label="Plan">
        <Select value={plan} onChange={(e) => setPlan(e.target.value)} required>
          {plans.map((p) => (
            <option key={p.code} value={p.code} disabled={!p.active}>
              {p.name}
              {p.active ? '' : ' (no longer offered)'}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Paid by" hint="A Stripe-paid licence starts its grace period when the subscription stops being paid.">
        <Select value={source} onChange={(e) => setSource(e.target.value as 'manual' | 'stripe')}>
          <option value="manual">An agreement with Hestaworks</option>
          <option value="stripe">The Stripe subscription</option>
        </Select>
      </Field>
      {source === 'manual' ? (
        <Field label="Valid until" hint="Leave empty for no end date. The grace period starts the day after.">
          <Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
        </Field>
      ) : null}
      <Field label="Grace period, in days" hint="How long stores keep working after the licence ends, with a warning in their admin.">
        <Input type="number" min={0} max={90} value={graceDays} onChange={(e) => setGraceDays(e.target.value)} required inputMode="numeric" />
      </Field>
      <Field
        label="Production domains"
        hint={chosen ? `Empty uses the plan's limit of ${chosen.production_domains}.` : 'Empty uses the plan’s limit.'}
      >
        <Input type="number" min={0} max={10000} value={domains} onChange={(e) => setDomains(e.target.value)} inputMode="numeric" />
      </Field>
      <Field label="Why" hint="For example the order or agreement. Shown in staff activity.">
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={500} required />
      </Field>
    </Dialog>
  );
}

function RevokeDialog({
  open,
  organizationId,
  onClose,
  onRevoked,
}: {
  open: boolean;
  organizationId: string;
  onClose: () => void;
  onRevoked: (view: OrganizationLicenceView) => void;
}) {
  const [mode, setMode] = useState<'grace' | 'immediate'>('grace');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const id = useId();

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      onRevoked(await staffApi.revokeLicence(organizationId, mode, reason));
      setReason('');
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ConfirmDialog
      open={open}
      title="Revoke the store licence"
      description="Its stores stop sending data and stop applying ERP updates, after the grace period or at once. The shops themselves keep running. Assigning a plan again lifts the revocation."
      confirmLabel="Revoke licence"
      danger
      busy={busy}
      confirmDisabled={reason.trim() === ''}
      onConfirm={() => void confirm()}
      onCancel={() => {
        if (!busy) onClose();
      }}
    >
      <ErrorNotice error={error} />
      <fieldset className="field staff-dialog__field">
        <legend className="field__label">When stores stop</legend>
        <Radio
          name={`${id}-mode`}
          checked={mode === 'grace'}
          onChange={() => setMode('grace')}
          label="After the grace period"
        />
        <Radio
          name={`${id}-mode`}
          checked={mode === 'immediate'}
          onChange={() => setMode('immediate')}
          label="At once"
        />
      </fieldset>
      <div className="field staff-dialog__field">
        <label className="field__label" htmlFor={`${id}-reason`}>
          Reason
        </label>
        <Textarea id={`${id}-reason`} value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500} required />
        <span className="field__hint muted">Staff only; members never see it.</span>
      </div>
    </ConfirmDialog>
  );
}
